import {
    DEFAULTS,
    toState,
    buildUrl,
    stateToApiQuery,
    parseFromLocation,
    applyHistory,
} from './urlState.js';
import {
    makeLineSeries,
    isSeriesEligible,
    createOfficialChart,
    deferInit,
} from './charts.js';

const EMPTY_LABEL = '—';
const FACT_UNAVAILABLE_LABEL = 'Nedostupno';
const PERIOD_UNAVAILABLE_LABEL = 'Period nije dostupan';

const SEGMENT_LABELS = Object.freeze({
    total: 'Ukupno',
    new_build: 'Novogradnja',
    old_build: 'Starogradnja',
});

const METRIC_LABELS = Object.freeze({
    yoy: 'Godišnja promena',
    qoq: 'Kvartalna promena',
    index: 'Kumulativno',
});

const MODE_LABELS = Object.freeze({
    nominal: 'Nominalno',
    real: 'Realno',
});

const BENCHMARK_LABELS = Object.freeze({
    none: 'Bez poređenja',
    spy: 'S&P 500',
    gld: 'Zlato',
    btc: 'Bitcoin',
});

const COMPARATIVE_BENCHMARKS = Object.freeze(['gld', 'spy', 'btc']);
const BENCHMARK_DOMINANCE_RATIO_THRESHOLD = 6;

const SERIES_STYLES = Object.freeze({
    marketNominal: {
        borderColor: '#0f5c66',
        backgroundColor: 'rgba(15, 92, 102, 0.08)',
        pointBackgroundColor: '#0f5c66',
    },
    marketReal: {
        borderColor: '#165b3f',
        backgroundColor: 'rgba(22, 91, 63, 0.08)',
        pointBackgroundColor: '#165b3f',
    },
    cpi: {
        borderColor: '#dc2626',
        backgroundColor: 'rgba(220, 38, 38, 0.08)',
        pointBackgroundColor: '#dc2626',
    },
    gld: {
        borderColor: '#d4af37',
        backgroundColor: 'rgba(212, 175, 55, 0.08)',
        pointBackgroundColor: '#d4af37',
    },
    spy: {
        borderColor: '#1d4ed8',
        backgroundColor: 'rgba(29, 78, 216, 0.08)',
        pointBackgroundColor: '#1d4ed8',
    },
    btc: {
        borderColor: '#f7931a',
        backgroundColor: 'rgba(247, 147, 26, 0.10)',
        pointBackgroundColor: '#f7931a',
    },
});

const BENCHMARK_ENDPOINT = '/api/market/benchmark';
const BENCHMARK_UNAVAILABLE_NOTE = 'Komparativni benchmark trenutno nije dostupan za izabranu kombinaciju. Prikazan je samo tržišni niz.';
const COMPARISON_PARTIAL_NOTE = 'Delimično dostupna komparativna analiza';
const BENCHMARK_FALLBACK_NOTE = 'fallback istorija: koristi se istorija iz alternativnog izvora.';
const BENCHMARK_STALE_NOTE = 'Za prikazano poređenje podaci su zastareli.';
const BENCHMARK_NEGATIVE_CACHE_TTL_MS = 30 * 1000;

const BENCHMARK_MODES = Object.freeze({
    quarterly: 'quarterly_pct',
    cumulative: 'cumulative_pct',
    base100: 'base100',
});

const VALID_BENCHMARK_VALUE_MODES = Object.freeze(['quarterly_pct', 'cumulative_pct', 'base100']);

const CPI_ENDPOINTS = Object.freeze({
    general: '/api/public/cpi-rs',
    quarterly: '/api/public/cpi-rs-quarterly',
    yearly: '/api/public/cpi-rs-yearly',
});

const CPI_FETCH_OPTIONS = Object.freeze({ cache: 'no-cache' });
const CPI_ESTIMATED_Q2_PERIOD_KEY = '2026-Q2';
const CPI_IMPORT_COMMAND = 'npm run import:rzs-cpi <path-to-json>';
const CPI_DIAGNOSE_COMMAND = 'npm run diagnose:market-cpi-benchmark';

const parseJsonNode = (id, fallback = {}) => {
    const node = document.getElementById(id);
    if (!node) {
        return fallback;
    }

    try {
        return JSON.parse(node.textContent || '{}');
    } catch (_error) {
        return fallback;
    }
};

const normalizeText = (value) => {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value).trim();
};

const toNumber = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === 'string' && value.trim() === '') {
        return null;
    }
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const firstNumber = (obj, keys = []) => {
    for (let i = 0; i < keys.length; i += 1) {
        const value = toNumber(obj?.[keys[i]]);
        if (value !== null) {
            return value;
        }
    }
    return null;
};

const firstText = (obj, keys = []) => {
    for (let i = 0; i < keys.length; i += 1) {
        const value = normalizeText(obj?.[keys[i]]);
        if (value) {
            return value;
        }
    }
    return '';
};

const percentFormat = (value, decimals = 2, signed = true) => {
    const number = toNumber(value);
    if (number === null) {
        return EMPTY_LABEL;
    }
    const formatter = new Intl.NumberFormat('sr-RS', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
    const sign = signed && number > 0 ? '+' : '';
    return `${sign}${formatter.format(number)}%`;
};

const decimalFormat = (value, decimals = 2) => {
    const number = toNumber(value);
    if (number === null) {
        return EMPTY_LABEL;
    }

    return new Intl.NumberFormat('sr-RS', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(number);
};

const integerFormat = (value) => {
    const number = toNumber(value);
    if (number === null) {
        return EMPTY_LABEL;
    }
    return new Intl.NumberFormat('sr-RS', {
        maximumFractionDigits: 0,
    }).format(number);
};

const eurPerSquareMeter = (value) => {
    const number = toNumber(value);
    if (number === null) {
        return EMPTY_LABEL;
    }
    return `${integerFormat(number)} €/m²`;
};

const billionEurFormat = (value) => {
    const number = toNumber(value);
    if (number === null) {
        return EMPTY_LABEL;
    }

    let billions;
    if (number >= 1e8) {
        billions = number / 1e9;
    } else if (number >= 100) {
        billions = number / 1000;
    } else {
        billions = number;
    }
    const formatter = new Intl.NumberFormat('sr-RS', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return `${formatter.format(billions)} mlrd €`;
};

const mapTrendPoint = (row) => ({
    quarter: firstText(row, ['quarter', 'reportPeriod', 'periodLabel']),
    periodLabel: firstText(row, ['periodLabel', 'reportPeriod', 'quarter']),
    index: firstNumber(row, ['index', 'indexValue', 'value']),
    yoyPct: firstNumber(row, ['yoyPct', 'yoy', 'yoyChangePct']),
    qoqPct: firstNumber(row, ['qoqPct', 'qoq', 'qoqChangePct']),
});

const mapRegionTrendPoint = (row) => ({
    quarter: firstText(row, ['quarter', 'periodLabel', 'reportPeriod']),
    yoyPct: firstNumber(row, ['yoyPct', 'yoy', 'yoyChangePct']),
});

const quarterTokens = (value) => {
    const text = normalizeText(value).toLowerCase();
    if (!text) {
        return null;
    }

    let match = text.match(/(\d{4})\s*-?\s*q([1-4])/i);
    if (match) {
        return {
            year: Number(match[1]),
            quarter: Number(match[2]),
        };
    }

    match = text.match(/q([1-4])\s*-?\s*(\d{4})/i);
    if (match) {
        return {
            year: Number(match[2]),
            quarter: Number(match[1]),
        };
    }

    match = text.match(/t([1-4])\s*(\d{4})/i);
    if (match) {
        return {
            year: Number(match[2]),
            quarter: Number(match[1]),
        };
    }

    match = text.match(/(\d{4})\s*t([1-4])/i);
    if (match) {
        return {
            year: Number(match[1]),
            quarter: Number(match[2]),
        };
    }

    return null;
};

const sameQuarterPreviousYearLabel = (value) => {
    const tokens = quarterTokens(value);
    if (!tokens) {
        return '';
    }

    return `T${tokens.quarter} ${tokens.year - 1}`;
};

const previousQuarterLabel = (value) => {
    const tokens = quarterTokens(value);
    if (!tokens) {
        return '';
    }

    if (tokens.quarter === 1) {
        return `T4 ${tokens.year - 1}`;
    }

    return `T${tokens.quarter - 1} ${tokens.year}`;
};

const quarterDisplayLabel = (value) => {
    const tokens = quarterTokens(value);
    if (!tokens) {
        return '';
    }

    return `T${tokens.quarter} ${tokens.year}`;
};

const quarterKeyFromText = (value) => {
    const parsed = quarterTokens(value);
    if (!parsed) {
        return '';
    }
    return `${parsed.year}-Q${parsed.quarter}`;
};

const quarterKeyFromDate = (value) => {
    const text = normalizeText(value);
    if (!text) {
        return '';
    }

    const match = text.match(/^(\d{4})-(\d{1,2})/);
    if (!match) {
        return '';
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
        return '';
    }

    const quarter = Math.floor((month - 1) / 3) + 1;
    return `${year}-Q${quarter}`;
};

const quarterKeyFromYear = (value) => {
    const number = toNumber(value);
    if (number === null) {
        return '';
    }

    const year = Math.trunc(number);
    if (!Number.isFinite(year) || year < 1000 || year > 9999) {
        return '';
    }

    return `${year}-Q4`;
};

const quarterLabelFromParts = (year, quarter) => {
    const y = Number(year);
    const q = Number(quarter);
    if (!Number.isFinite(y) || !Number.isFinite(q) || q < 1 || q > 4) {
        return '';
    }
    return `T${q} ${y}`;
};

const periodKeyFromPoint = (row, options = {}) => {
    if (!row || typeof row !== 'object') {
        return '';
    }

    const allowYearAsQ4 = options.allowYearAsQ4 === true;

    const explicitDate = firstText(row, ['date']);
    const dateKey = quarterKeyFromDate(explicitDate);
    if (dateKey) {
        return dateKey;
    }

    const explicitQuarter = quarterLabelFromParts(row.year, row.quarter);
    if (explicitQuarter) {
        return quarterKeyFromText(explicitQuarter);
    }

    const candidate =
        firstText(row, ['quarter', 'periodLabel', 'reportPeriod', 'period', 'label', 'date'])
        || firstText(row, ['yearLabel']);

    if (!candidate) {
        if (!allowYearAsQ4) {
            return '';
        }

        return quarterKeyFromYear(firstText(row, ['year', 'yearLabel']));
    }

    const quarterKey = quarterKeyFromText(candidate);
    if (quarterKey) {
        return quarterKey;
    }

    const monthlyQuarterKey = quarterKeyFromDate(candidate);
    if (monthlyQuarterKey) {
        return monthlyQuarterKey;
    }

    if (!allowYearAsQ4) {
        return '';
    }

    return quarterKeyFromYear(candidate);
};

const findFirstArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }
    if (!value || typeof value !== 'object') {
        return [];
    }

    const keys = Object.keys(value);
    for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        const candidate = value[key];
        if (Array.isArray(candidate)) {
            return candidate;
        }
    }

    for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        const candidate = value[key];
        if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
            const nested = findFirstArray(candidate);
            if (nested.length) {
                return nested;
            }
        }
    }

    return [];
};

const CPI_YOY_METRIC_TYPES = Object.freeze(['inflation_yoy_pct', 'yoy_pct']);

const isCpiYoyMetricType = (metricType) => CPI_YOY_METRIC_TYPES.includes(metricType);

const mapCpiPoint = (row, meta = {}) => {
    const metricType = firstText(row, ['metricType', 'metric_type']) || '';
    const hasQuarterlyFields =
        firstNumber(row, [
            'quarter_end_cpi_base_2006',
            'quarter_avg_cpi',
            'inflation_qoq_pct',
            'inflation_yoy_q_end_pct',
            'inflation_yoy_q_avg_pct',
            'inflation_q_compounded_pct',
        ]) !== null
        || isCpiYoyMetricType(metricType)
        || Boolean(firstText(row, ['unit', 'status']));
    const hasYearlyFields = firstNumber(row, ['avg_cpi_index', 'inflation_avg_year_pct']) !== null;

    const derivedPeriodLabel =
        firstText(row, ['quarter'])
        || quarterLabelFromParts(row?.year, row?.quarter)
        || (() => {
            const dateKey = quarterKeyFromDate(firstText(row, ['date']));
            if (!dateKey) {
                return '';
            }
            const parsed = quarterTokens(dateKey);
            return parsed ? quarterLabelFromParts(parsed.year, parsed.quarter) : '';
        })()
        || (() => {
            const yearQ4 = quarterKeyFromYear(firstText(row, ['year']));
            if (!yearQ4) {
                return '';
            }
            const parsed = quarterTokens(yearQ4);
            return parsed ? quarterLabelFromParts(parsed.year, parsed.quarter) : '';
        })()
        || firstText(row, ['periodLabel', 'reportPeriod', 'period', 'label', 'date']);

    const periodLabel = derivedPeriodLabel;
    const periodKey = periodKeyFromPoint(row, { allowYearAsQ4: hasYearlyFields && !hasQuarterlyFields });

    const index = firstNumber(row, [
        'quarter_avg_cpi',
        'quarter_end_cpi_base_2006',
        'cpi_base_2006',
        'avg_cpi_index',
        'index',
        'indexValue',
        'cpiIndex',
        'value',
    ]);

    const yoyPct = firstNumber(row, [
        'inflation_yoy_q_avg_pct',
        'inflation_yoy_pct',
        'inflation_yoy_q_end_pct',
        'inflation_avg_year_pct',
        'yoyPct',
        'yoy',
        'yoyChangePct',
        'inflationYoyPct',
        'inflationPct',
    ]);

    const qoqPct = firstNumber(row, [
        'inflation_qoq_pct',
        'qoqPct',
        'qoq',
        'qoqChangePct',
        'inflationQoqPct',
    ]);

    const status = firstText(row, ['status']) || '';
    const isEstimated = row?.isEstimated === true
        || row?.is_estimated === true
        || row?.isEstimated === 1
        || row?.is_estimated === 1
        || status === 'estimated';

    return {
        periodLabel,
        periodKey,
        index,
        yoyPct,
        qoqPct,
        metricType,
        unit: firstText(row, ['unit']) || '',
        status,
        isEstimated,
        statusNote: firstText(row, ['statusNote', 'status_note']) || '',
        sourceCode: firstText(row, ['sourceCode', 'source_code']) || firstText(meta, ['sourceCode', 'source_code', 'source']) || '',
        classification: firstText(row, ['classification']) || firstText(meta, ['classification']) || '',
        baseYear: firstNumber(row, ['baseYear', 'base_year']) ?? firstNumber(meta, ['baseYear', 'base_year']),
    };
};

const normalizeCpiSeries = (body) => {
    const rows = findFirstArray(body);
    const meta = body && typeof body === 'object' && !Array.isArray(body)
        ? (body.meta && typeof body.meta === 'object' ? body.meta : {})
        : {};
    return rows
        .map((row) => mapCpiPoint(row, meta))
        .filter((row) => (
            row.periodKey
            && (
                row.index !== null
                || row.yoyPct !== null
                || row.qoqPct !== null
                || row.status === 'not_available'
                || row.status === 'estimated'
                || row.isEstimated === true
                || row.metricType
                || row.sourceCode
                || row.classification
                || row.baseYear !== null
            )
        ));
};

const requestCpiEndpoint = async (endpoint, fetchImpl = fetch) => {
    try {
        const response = await fetchImpl(endpoint, CPI_FETCH_OPTIONS);
        if (!response || !response.ok) {
            return [];
        }

        const body = await response.json();
        return normalizeCpiSeries(body);
    } catch (_error) {
        return [];
    }
};

const createCpiStore = (initial = {}) => ({
    general: initial.general,
    quarterly: initial.quarterly,
    yearly: initial.yearly,
    diagnostics: initial.diagnostics || null,
});

const mergeCpiSeries = (current, incoming) => {
    const byPeriod = new Map();

    (Array.isArray(current) ? current : []).forEach((row) => {
        if (row?.periodKey) {
            byPeriod.set(row.periodKey, row);
        }
    });

    (Array.isArray(incoming) ? incoming : []).forEach((row) => {
        if (!row?.periodKey) {
            return;
        }

        const existing = byPeriod.get(row.periodKey) || {};
        byPeriod.set(row.periodKey, {
            ...existing,
            ...row,
        });
    });

    return Array.from(byPeriod.values()).sort((left, right) => (
        String(left.periodKey).localeCompare(String(right.periodKey))
    ));
};

const buildCpiStoreDiagnostics = (store, latestPeriodKey = '') => {
    const quarterly = Array.isArray(store?.quarterly) ? store.quarterly : [];
    const latestEntry = quarterly.find((entry) => entry?.periodKey === latestPeriodKey) || null;
    const estimatedQ2 = quarterly.find((entry) => entry?.periodKey === CPI_ESTIMATED_Q2_PERIOD_KEY) || null;
    const latestCovered = cpiSeriesCoversPeriod(quarterly, latestPeriodKey);
    const estimatedQ2Present = cpiEntryHasFiniteValue(estimatedQ2);

    return {
        latestPeriodKey,
        quarterlyCount: quarterly.length,
        latestCovered,
        estimatedQ2Present,
        estimatedQ2YoyPct: estimatedQ2Present ? estimatedQ2.yoyPct : null,
        seedRequired: latestPeriodKey === CPI_ESTIMATED_Q2_PERIOD_KEY && !estimatedQ2Present,
        importCommand: CPI_IMPORT_COMMAND,
        diagnoseCommand: CPI_DIAGNOSE_COMMAND,
        latestEntryStatus: latestEntry?.status || null,
    };
};

const cpiEntryHasFiniteValue = (entry) => (
    toNumber(entry?.yoyPct) !== null
    || toNumber(entry?.qoqPct) !== null
    || toNumber(entry?.index) !== null
);

const cpiSeriesCoversPeriod = (series, periodKey) => {
    const rows = Array.isArray(series) ? series : [];
    if (!periodKey) {
        return rows.length > 0;
    }

    return rows.some((entry) => entry?.periodKey === periodKey && cpiEntryHasFiniteValue(entry));
};

const latestMarketPeriodKeyFromPayload = (payload) => {
    const points = Array.isArray(payload?.nationalTrend?.points) ? payload.nationalTrend.points : [];
    if (points.length > 0) {
        const fromLastPoint = periodKeyFromPoint(points[points.length - 1]);
        if (fromLastPoint) {
            return fromLastPoint;
        }
    }

    const latest = payload?.nationalTrend?.latest;
    const fromLatest = periodKeyFromPoint(latest || {});
    if (fromLatest) {
        return fromLatest;
    }

    return periodKeyFromPoint({
        reportPeriod: payload?.reportPeriod,
        quarter: payload?.hero?.period,
        periodLabel: payload?.hero?.period,
    }) || '';
};

const shouldRefreshCpiSeries = (series, latestPeriodKey, force = false) => {
    if (force === true) {
        return true;
    }

    if (!Array.isArray(series)) {
        return true;
    }

    return !cpiSeriesCoversPeriod(series, latestPeriodKey);
};

const fetchCpiSeries = async (cpiStore, key, endpoint, options = {}) => {
    const store = cpiStore && typeof cpiStore === 'object' ? cpiStore : {};
    const force = options.force === true;
    if (!force && store[key] !== undefined) {
        return store[key];
    }

    const series = await requestCpiEndpoint(endpoint, options.fetchImpl);
    if (Array.isArray(series) && series.length > 0) {
        store[key] = mergeCpiSeries(store[key], series);
        return store[key];
    }

    if (store[key] === undefined) {
        store[key] = [];
    }

    return store[key];
};

const ensureCpiData = async (cpiStore, metric, options = {}) => {
    const store = cpiStore && typeof cpiStore === 'object' ? cpiStore : createCpiStore();
    const latestPeriodKey = options.latestPeriodKey || '';
    const forceQuarterly = options.forceQuarterly === true
        || shouldRefreshCpiSeries(store.quarterly, latestPeriodKey);

    await fetchCpiSeries(store, 'quarterly', CPI_ENDPOINTS.quarterly, {
        force: forceQuarterly,
        fetchImpl: options.fetchImpl,
    });

    if (metric === 'index') {
        await Promise.all([
            fetchCpiSeries(store, 'yearly', CPI_ENDPOINTS.yearly, { fetchImpl: options.fetchImpl }),
            fetchCpiSeries(store, 'general', CPI_ENDPOINTS.general, { fetchImpl: options.fetchImpl }),
        ]);
        store.diagnostics = buildCpiStoreDiagnostics(store, latestPeriodKey);
        return store;
    }

    await fetchCpiSeries(store, 'general', CPI_ENDPOINTS.general, { fetchImpl: options.fetchImpl });
    if (metric === 'yoy') {
        await fetchCpiSeries(store, 'yearly', CPI_ENDPOINTS.yearly, { fetchImpl: options.fetchImpl });
    }

    store.diagnostics = buildCpiStoreDiagnostics(store, latestPeriodKey);
    return store;
};

const mapRegionRow = (row) => ({
    scopeCode: firstText(row, ['scopeCode', 'scope_code', 'code']),
    scopeLabelSr: firstText(row, ['scopeLabelSr', 'label', 'name']),
    yoyPct: firstNumber(row, ['yoyPct', 'yoy']),
    qoqPct: firstNumber(row, ['qoqPct', 'qoq']),
    index: firstNumber(row, ['index', 'indexValue', 'value']),
    previousYoyPct:
        firstNumber(row, ['previousYoyPct', 'prevYoyPct', 'yoyPctPrev'])
        ?? firstNumber(row?.previous, ['yoyPct', 'yoy'])
        ?? null,
    trend: Array.isArray(row?.trend) ? row.trend.map(mapRegionTrendPoint) : [],
});

const cityStats = (row, segment) => {
    const fromSegments = row?.segments?.[segment] || {};
    const fromStats = row?.stats || {};
    const fromFlat = row?.[segment] || {};

    const pick = (keys = []) => {
        for (let i = 0; i < keys.length; i += 1) {
            const key = keys[i];
            const value =
                firstNumber(fromSegments, [key])
                ?? firstNumber(fromStats, [key])
                ?? firstNumber(fromFlat, [key])
                ?? firstNumber(row, [key]);
            if (value !== null) {
                return value;
            }
        }
        return null;
    };

    return {
        p10: pick(['p10_eur_m2', 'p10', 'priceP10EurM2']),
        median: pick(['median_eur_m2', 'median', 'priceMedianEurM2']),
        p90: pick(['p90_eur_m2', 'p90', 'priceP90EurM2']),
    };
};

const mapCityRow = (row) => ({
    cityCode: firstText(row, ['cityCode', 'city_code', 'code']),
    cityLabelSr: firstText(row, ['cityLabelSr', 'city', 'name']),
    reportPeriod: firstText(row, ['reportPeriod', 'periodLabel']),
    new_build: cityStats(row, 'new_build'),
    old_build: cityStats(row, 'old_build'),
    total: cityStats(row, 'total'),
});

const normalizePayload = ({ overview, insights, shareSnippet, copy, query, renderVersion }) => {
    const nationalPoints = overview?.nationalTrend?.points || overview?.trend?.points || [];
    const selectedRegionTrend = overview?.regionalSnapshot?.selectedRegion?.trend || [];
    const trendPoints = nationalPoints.length > 0 ? nationalPoints : selectedRegionTrend;
    const regions = overview?.regionalSnapshot?.regions || overview?.regions || [];
    const cities = overview?.largeCitySnapshot?.cities || overview?.velikiGradoviQ4 || [];
    const latestTrend = (trendPoints.length ? trendPoints[trendPoints.length - 1] : null)
        || overview?.nationalTrend?.latest
        || null;

    return {
        renderVersion: normalizeText(overview?.renderVersion || insights?.renderVersion || renderVersion),
        canonicalPath: normalizeText(overview?.canonicalPath || insights?.canonicalPath) || '/market',
        query: query || overview?.query || insights?.query || {},
        copy: copy || {},
        share: insights?.share || shareSnippet || {},
        hero: insights?.hero || overview?.hero || {},
        interpretation: firstText(insights, ['summary']) || firstText(overview, ['summary']),
        leadCopy:
            firstText(insights?.hero, ['lead'])
            || firstText(copy, ['lead'])
            || 'Zvanični pregled kretanja cena i obima tržišta za poslednji objavljeni kvartal.',
        contextCopy:
            firstText(insights?.hero, ['context'])
            || firstText(copy, ['context'])
            || 'Podaci su prikazani kroz godišnje i kvartalne cikluse, uz dodatni presek po regionima i većim gradovima.',
        reportPeriod:
            firstText(overview?.releaseFacts, ['periodLabel', 'reportPeriod'])
            || firstText(insights?.hero, ['period'])
            || firstText(latestTrend, ['periodLabel', 'reportPeriod', 'quarter'])
            || '',
        nationalTrend: {
            points: (Array.isArray(trendPoints) ? trendPoints : []).map(mapTrendPoint),
            latest: mapTrendPoint(latestTrend || {}),
        },
        regionalRows: (Array.isArray(regions) ? regions : []).map(mapRegionRow).filter((row) => row.scopeLabelSr),
        cityRows: (Array.isArray(cities) ? cities : []).map(mapCityRow).filter((row) => row.cityLabelSr),
        releaseFacts: mergeReleaseFacts(insights?.releaseFacts, overview?.releaseFacts),
        availability: overview?.availability || insights?.availability || {},
    };
};

const metricConfig = (metric) => {
    if (metric === 'qoq') {
        return {
            key: 'qoqPct',
            label: METRIC_LABELS.qoq,
            suffix: '%',
            decimals: 2,
        };
    }

    if (metric === 'index') {
        return {
            key: 'index',
            label: METRIC_LABELS.index,
            suffix: '%',
            decimals: 2,
        };
    }

    return {
        key: 'yoyPct',
        label: METRIC_LABELS.yoy,
        suffix: '%',
        decimals: 2,
    };
};

const createApiUrl = (path, state) => {
    const params = new URLSearchParams();
    const query = stateToApiQuery(state);
    Object.keys(query).sort().forEach((key) => {
        const value = query[key];
        if (value !== '' && value !== null && value !== undefined) {
            params.set(key, value);
        }
    });
    const queryString = params.toString();
    return queryString ? `${path}?${queryString}` : path;
};

const normalizeBenchmarkModeForMetric = (metric, benchmarkMode) => {
    const normalizedMode = benchmarkMode === BENCHMARK_MODES.base100
        ? BENCHMARK_MODES.base100
        : benchmarkMode === BENCHMARK_MODES.quarterly
            ? BENCHMARK_MODES.quarterly
            : BENCHMARK_MODES.cumulative;

    if (metric === 'qoq') {
        return BENCHMARK_MODES.quarterly;
    }

    if (metric === 'index') {
        return normalizedMode === BENCHMARK_MODES.base100
            ? BENCHMARK_MODES.base100
            : BENCHMARK_MODES.cumulative;
    }

    return normalizedMode;
};

const enforceUiBenchmarkMode = (ui) => {
    if (!ui || typeof ui !== 'object') {
        return false;
    }

    const normalized = normalizeBenchmarkModeForMetric(ui.metric, ui.benchmarkMode);
    if (normalized === ui.benchmarkMode) {
        return false;
    }

    ui.benchmarkMode = normalized;
    return true;
};

const createBenchmarkApiUrl = (state, metric, mode, benchmarkCode, benchmarkMode) => {
    const params = new URLSearchParams();
    const query = stateToApiQuery(state);

    Object.keys(query).sort().forEach((key) => {
        const value = query[key];
        if (value !== '' && value !== null && value !== undefined) {
            params.set(key, value);
        }
    });

    params.set('metric', metric);
    params.set('mode', mode);
    params.set('benchmark', benchmarkCode);
    params.set('benchmarkMode', normalizeBenchmarkModeForMetric(metric, benchmarkMode));

    return `${BENCHMARK_ENDPOINT}?${params.toString()}`;
};

const benchmarkRequestKey = (state, metric, mode, benchmarkCode, benchmarkMode) => {
    const params = new URLSearchParams();
    const query = stateToApiQuery(state);

    Object.keys(query).sort().forEach((key) => {
        const value = query[key];
        if (value !== '' && value !== null && value !== undefined) {
            params.set(key, value);
        }
    });

    params.set('benchmark', benchmarkCode);
    params.set('benchmarkMode', normalizeBenchmarkModeForMetric(metric, benchmarkMode));
    params.set('metric', metric);
    params.set('mode', mode);

    return params.toString();
};

const fetchPayload = async (state, currentPayload) => {
    const [overviewResponse, insightsResponse] = await Promise.all([
        fetch(createApiUrl('/api/market/overview', state)),
        fetch(createApiUrl('/api/market/insights', state)),
    ]);

    if (!overviewResponse.ok || !insightsResponse.ok) {
        throw new Error('MARKET_FETCH_FAILED');
    }

    const overviewBody = await overviewResponse.json();
    const insightsBody = await insightsResponse.json();

    return normalizePayload({
        overview: overviewBody.overview || overviewBody,
        insights: insightsBody.insights || insightsBody,
        shareSnippet: insightsBody.share || insightsBody.shareSnippet || {},
        copy: insightsBody.copy || overviewBody.copy || currentPayload.copy || {},
        query: overviewBody.query || insightsBody.query || currentPayload.query,
        renderVersion: currentPayload.renderVersion,
    });
};

const buildShareableMarketUrl = (state, locationLike = window.location) => {
    const origin = locationLike?.origin || window.location.origin;
    const path = buildUrl(state, DEFAULTS, {
        pathname: '/market',
        hash: locationLike?.hash || '',
        includeDefaults: false,
    });
    return `${origin}${path}`;
};

const destroyChart = (chartStore) => {
    if (chartStore.current && typeof chartStore.current.destroy === 'function') {
        chartStore.current.destroy();
    }
    chartStore.current = null;
};

const syncSegmentSwitch = (state) => {
    const group = document.querySelector('[data-role="segment-switch"]');
    if (!group) {
        return;
    }

    group.querySelectorAll('button[data-value]').forEach((button) => {
        const selected = normalizeText(button.getAttribute('data-value')).toLowerCase() === state.segment;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
};

const syncMetricSwitch = (metric) => {
    const group = document.querySelector('[data-role="metric-switch"]');
    if (!group) {
        return;
    }

    group.querySelectorAll('button[data-value]').forEach((button) => {
        const selected = normalizeText(button.getAttribute('data-value')).toLowerCase() === metric;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
};

const syncModeSwitch = (mode) => {
    const group = document.querySelector('[data-role="mode-switch"]');
    if (!group) {
        return;
    }

    group.querySelectorAll('button[data-value]').forEach((button) => {
        const selected = normalizeText(button.getAttribute('data-value')).toLowerCase() === mode;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
};

const syncComparisonToggle = (ui) => {
    const toggle = document.getElementById('comparison-toggle-input');
    if (!toggle) {
        return;
    }

    const checked = Boolean(ui?.compare);
    toggle.checked = checked;
    toggle.setAttribute('aria-checked', checked ? 'true' : 'false');
};

const releaseFactEntryValue = (entry) => {
    if (entry === null || entry === undefined) {
        return null;
    }
    if (typeof entry === 'object' && !Array.isArray(entry)) {
        if (Object.prototype.hasOwnProperty.call(entry, 'value')) {
            return entry.value;
        }
        return null;
    }
    return entry;
};

const collectReleaseFactEntries = (releaseFacts) => {
    if (Array.isArray(releaseFacts)) {
        return releaseFacts;
    }
    if (!releaseFacts || typeof releaseFacts !== 'object') {
        return [];
    }

    const entries = [];
    if (Array.isArray(releaseFacts.rows)) {
        entries.push(...releaseFacts.rows);
    }
    if (releaseFacts.metrics && typeof releaseFacts.metrics === 'object' && !Array.isArray(releaseFacts.metrics)) {
        Object.keys(releaseFacts.metrics).forEach((key) => {
            entries.push({
                key,
                label: key,
                value: releaseFactEntryValue(releaseFacts.metrics[key]),
            });
        });
    }
    return entries;
};

const firstReleaseFactField = (sources, keys) => {
    for (let i = 0; i < sources.length; i += 1) {
        const source = sources[i];
        if (!source || typeof source !== 'object' || Array.isArray(source)) {
            continue;
        }
        const value = firstText(source, keys);
        if (value) {
            return value;
        }
    }
    return '';
};

const mergeReleaseFacts = (...sources) => {
    const entries = [];
    sources.forEach((source) => {
        collectReleaseFactEntries(source).forEach((row) => {
            entries.push(row);
        });
    });

    const metrics = {};
    entries.forEach((row) => {
        const key = normalizeText(row?.key);
        if (!key || Object.prototype.hasOwnProperty.call(metrics, key)) {
            return;
        }
        const numeric = toNumber(row?.value);
        if (numeric === null) {
            return;
        }
        metrics[key] = {
            value: numeric,
        };
    });

    return {
        reportPeriod: firstReleaseFactField(sources, ['reportPeriod']) || null,
        periodLabel: firstReleaseFactField(sources, ['periodLabel']) || null,
        rows: entries,
        metrics,
    };
};

const findReleaseFactRaw = (releaseFacts, patterns) => {
    const rows = collectReleaseFactEntries(releaseFacts);

    for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i] || {};
        const label = normalizeText(row.label || row.key).toLowerCase();
        const value = normalizeText(row.value);
        if (!label || !value) {
            continue;
        }

        const matched = patterns.some((pattern) => pattern.test(label));
        if (matched) {
            return value;
        }
    }

    return '';
};

const findReleaseFactNumeric = (releaseFacts, keys) => {
    const rows = collectReleaseFactEntries(releaseFacts);
    for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i] || {};
        const key = normalizeText(row.key || '').toLowerCase();
        if (!keys.some((k) => key === k)) {
            continue;
        }
        const numeric = toNumber(row.value);
        if (numeric !== null) {
            return numeric;
        }
    }
    return null;
};

const INDEPENDENT_HERO_METRIC_KEYS = Object.freeze({
    index: ['index'],
    yoy_pct: ['yoy_pct', 'yoy', 'yoypct'],
    qoq_pct: ['qoq_pct', 'qoq', 'qoqpct'],
    transactions_count: ['transactions_count'],
    market_value_eur_m: ['market_value_eur_m'],
    credit_share_pct: ['credit_share_pct'],
    credit_transactions_count: ['credit_transactions_count'],
});

const normalizeIndependentHeroFacts = (releaseFacts) => ({
    reportPeriod: firstText(releaseFacts, ['reportPeriod']) || '',
    periodLabel: firstText(releaseFacts, ['periodLabel']) || '',
    index: findReleaseFactNumeric(releaseFacts, INDEPENDENT_HERO_METRIC_KEYS.index),
    yoy_pct: findReleaseFactNumeric(releaseFacts, INDEPENDENT_HERO_METRIC_KEYS.yoy_pct),
    qoq_pct: findReleaseFactNumeric(releaseFacts, INDEPENDENT_HERO_METRIC_KEYS.qoq_pct),
    transactions_count: findReleaseFactNumeric(releaseFacts, INDEPENDENT_HERO_METRIC_KEYS.transactions_count),
    market_value_eur_m: findReleaseFactNumeric(releaseFacts, INDEPENDENT_HERO_METRIC_KEYS.market_value_eur_m),
    credit_share_pct: findReleaseFactNumeric(releaseFacts, INDEPENDENT_HERO_METRIC_KEYS.credit_share_pct),
    credit_transactions_count: findReleaseFactNumeric(releaseFacts, INDEPENDENT_HERO_METRIC_KEYS.credit_transactions_count),
});

const resolveIndependentHeroPeriod = (independent, payload) => {
    const source =
        normalizeText(independent?.reportPeriod)
        || normalizeText(independent?.periodLabel)
        || normalizeText(payload?.reportPeriod);
    return quarterDisplayLabel(source) || source;
};

const extractHeroFacts = (payload) => {
    const hero = payload?.hero || {};
    const latestTrend = payload?.nationalTrend?.latest || {};
    const releaseFacts = payload?.releaseFacts || {};
    const independent = normalizeIndependentHeroFacts(releaseFacts);

    const yoy = independent.yoy_pct ?? firstNumber(hero, ['yoyPct', 'yoy']) ?? latestTrend.yoyPct;
    const qoq = independent.qoq_pct ?? firstNumber(hero, ['qoqPct', 'qoq']) ?? latestTrend.qoqPct;

    const salesCount =
        independent.transactions_count
        ?? firstNumber(hero, ['salesCount', 'sales', 'purchaseCount'])
        ?? firstNumber(payload?.share, ['salesCount'])
        ?? null;

    const loanShare =
        independent.credit_share_pct
        ?? firstNumber(hero, ['loanSharePct', 'loanPurchaseSharePct', 'creditSharePct'])
        ?? firstNumber(payload?.share, ['loanSharePct', 'creditSharePct'])
        ?? null;

    const marketValue =
        independent.market_value_eur_m
        ?? firstNumber(hero, ['marketValueEurM', 'marketValueBillionEur', 'marketValueEur'])
        ?? null;

    const loanSales =
        independent.credit_transactions_count
        ?? firstNumber(hero, ['loanSalesCount', 'loanPurchasesCount', 'creditTransactionsCount'])
        ?? null;

    // RGZ publishes the credit purchase share but not the credit transaction count.
    // When the official count is missing, derive a transparent estimate from the
    // official transaction count and the official credit share (see methodology panel).
    const derivedLoanSales =
        loanSales === null && salesCount !== null && loanShare !== null
            ? Math.round((salesCount * loanShare) / 100)
            : null;

    return {
        yoy: yoy !== null ? percentFormat(yoy, 2, true) : FACT_UNAVAILABLE_LABEL,
        qoq: qoq !== null ? percentFormat(qoq, 2, true) : FACT_UNAVAILABLE_LABEL,
        salesCount:
            salesCount !== null
                ? integerFormat(salesCount)
                : findReleaseFactRaw(releaseFacts, [/kupoprodaj/, /promet stanova/, /broj transakcija/]) || FACT_UNAVAILABLE_LABEL,
        loanShare:
            loanShare !== null
                ? percentFormat(loanShare, 1, false)
                : FACT_UNAVAILABLE_LABEL,
        marketValue:
            marketValue !== null
                ? billionEurFormat(marketValue)
                : FACT_UNAVAILABLE_LABEL,
        loanSales:
            loanSales !== null
                ? integerFormat(loanSales)
                : derivedLoanSales !== null
                    ? integerFormat(derivedLoanSales)
                    : FACT_UNAVAILABLE_LABEL,
        loanSalesDerived: loanSales === null && derivedLoanSales !== null,
        reportPeriod: independent.reportPeriod,
        periodLabel: independent.periodLabel,
        period: resolveIndependentHeroPeriod(independent, payload),
    };
};

const setText = (id, text) => {
    const node = document.getElementById(id);
    if (node) {
        node.textContent = normalizeText(text);
    }
};

const renderHero = (payload) => {
    const facts = extractHeroFacts(payload);
    const period = normalizeText(facts.period);
    const periodLabel = period || PERIOD_UNAVAILABLE_LABEL;
    const yoyReference = sameQuarterPreviousYearLabel(period);
    const qoqReference = previousQuarterLabel(period);

    setText('market-period-meta', `Presek: ${periodLabel}`);
    setText('hero-lead-copy', payload?.leadCopy);
    setText('hero-context-copy', payload?.contextCopy || payload?.interpretation);

    setText('fact-yoy-value', facts.yoy);
    setText('fact-qoq-value', facts.qoq);
    setText('fact-sales-value', facts.salesCount);
    setText('fact-loan-share-value', facts.loanShare);
    setText('fact-market-value', facts.marketValue);
    setText('fact-loan-sales-value', facts.loanSales);

    setText('fact-yoy-note', yoyReference ? `u odnosu na ${yoyReference}` : 'Poređenje nije dostupno (nedostaje period).');
    setText('fact-qoq-note', qoqReference ? `u odnosu na ${qoqReference}` : 'Poređenje nije dostupno (nedostaje period).');
    setText('fact-sales-note', period ? `stanova u ${period}` : 'Period objave nije dostupan.');
    setText('fact-loan-share-note', period ? `udeo od svih kupoprodaja u ${period}` : 'Period objave nije dostupan.');
    setText('fact-market-note', period ? `u ${period}` : 'Period objave nije dostupan.');
    setText(
        'fact-loan-sales-note',
        facts.loanSalesDerived
            ? (period
                ? `procena iz zvaničnog udela kreditnih kupovina u ${period}`
                : 'Procena iz zvaničnog udela kreditnih kupovina.')
            : (period ? `stanova u ${period}` : 'Period objave nije dostupan.')
    );
};

const metricValueFromPoint = (point, metric) => {
    const config = metricConfig(metric);
    return point?.[config.key] ?? null;
};

const renderCompareStrip = (segmentPayloads) => {
    const readValues = (segment) => {
        const payload = segmentPayloads[segment] || null;
        const latestPoint = payload?.nationalTrend?.latest || null;
        return {
            yoy: latestPoint?.yoyPct ?? payload?.hero?.yoyPct ?? null,
            qoq: latestPoint?.qoqPct ?? payload?.hero?.qoqPct ?? null,
        };
    };

    const newBuild = readValues('new_build');
    const oldBuild = readValues('old_build');

    setText('compare-new-yoy', `Godišnja promena: ${percentFormat(newBuild.yoy, 2, true)}`);
    setText('compare-new-qoq', `Kvartalna promena: ${percentFormat(newBuild.qoq, 2, true)}`);
    setText('compare-old-yoy', `Godišnja promena: ${percentFormat(oldBuild.yoy, 2, true)}`);
    setText('compare-old-qoq', `Kvartalna promena: ${percentFormat(oldBuild.qoq, 2, true)}`);
};

const deriveRegionCurrentAndPrev = (row) => {
    const trend = Array.isArray(row?.trend) ? row.trend : [];
    if (trend.length >= 2) {
        const valid = trend
            .map((point) => ({
                quarter: point?.quarter || '',
                yoyPct: toNumber(point?.yoyPct),
            }))
            .filter((point) => point.yoyPct !== null);

        if (valid.length >= 2) {
            const current = valid[valid.length - 1].yoyPct;
            const previous = valid[valid.length - 2].yoyPct;
            return { current, previous };
        }
    }

    return {
        current: toNumber(row?.yoyPct),
        previous: toNumber(row?.previousYoyPct),
    };
};

const computeStatus = (current, previous) => {
    if (current === null || previous === null) {
        return {
            label: '',
            className: '',
            description: 'Nema dovoljno podataka za poređenje',
        };
    }

    const delta = current - previous;
    if (delta > 1.0) {
        return {
            label: 'Ubrzava',
            className: 'status-badge status-badge--rise',
                description: `${percentFormat(previous, 2, false)} -> ${percentFormat(current, 2, false)}`,
        };
    }

    if (delta < -1.0) {
        return {
            label: 'Usporava',
            className: 'status-badge status-badge--fall',
                description: `${percentFormat(previous, 2, false)} -> ${percentFormat(current, 2, false)}`,
        };
    }

    return {
        label: 'Stabilno',
        className: 'status-badge status-badge--flat',
            description: `${percentFormat(previous, 2, false)} -> ${percentFormat(current, 2, false)}`,
    };
};

const renderRegionTable = (payload) => {
    const body = document.getElementById('region-table-body');
    if (!body) {
        return;
    }

    const reportPeriod = normalizeText(payload?.reportPeriod);
    if (reportPeriod) {
        const displayPeriod = quarterDisplayLabel(reportPeriod) || reportPeriod;
        const currentHeader = document.getElementById('region-col-current');
        if (currentHeader) {
            currentHeader.textContent = `God. promena ${displayPeriod}`;
        }

        const previousReportPeriod = previousQuarterLabel(reportPeriod);
        if (previousReportPeriod) {
            const previousHeader = document.getElementById('region-col-prev');
            if (previousHeader) {
                previousHeader.textContent = `God. promena ${previousReportPeriod}`;
            }
        }
    }

    const rows = Array.isArray(payload?.regionalRows) ? payload.regionalRows : [];

    const panel = document.querySelector('.region-table-panel');
    if (panel) {
        panel.hidden = rows.length === 0;
    }

    body.innerHTML = rows
        .map((row) => {
            const { current, previous } = deriveRegionCurrentAndPrev(row);
            const status = computeStatus(current, previous);
            const currentLabel = percentFormat(current, 2, false);
            const previousLabel = percentFormat(previous, 2, false);
            const statusCell = status.label
                ? `<span class="${status.className}">${status.label}</span><div class="status-note">${status.description}</div>`
                : `<span class="status-note">${status.description}</span>`;

            return `
                <tr class="region-table__row">
                    <td class="region-name">${row.scopeLabelSr}</td>
                    <td class="num">${previousLabel}</td>
                    <td class="num">${currentLabel}</td>
                    <td class="status-cell">${statusCell}</td>
                </tr>
            `;
        })
        .join('');
};

const chooseRange = (row) => {
    const candidates = [row?.new_build, row?.old_build];
    for (let i = 0; i < candidates.length; i += 1) {
        const sample = candidates[i] || {};
        const p10 = toNumber(sample.p10);
        const p90 = toNumber(sample.p90);
        if (p10 !== null && p90 !== null) {
            return `${integerFormat(p10)}-${integerFormat(p90)} €/m²`;
        }
    }
    return EMPTY_LABEL;
};

const renderCityTable = (payload) => {
    const body = document.getElementById('city-table-body');
    if (!body) {
        return;
    }

    const rows = Array.isArray(payload?.cityRows) ? payload.cityRows : [];

    const panel = document.querySelector('.city-table-panel');
    if (panel) {
        panel.hidden = rows.length === 0;
    }

    body.innerHTML = rows
        .map((row) => {
            const newBuild = eurPerSquareMeter(row?.new_build?.median);
            const oldBuild = eurPerSquareMeter(row?.old_build?.median);
            const range = chooseRange(row);

            return `
                <tr class="city-table__row">
                    <td class="city-name">${row.cityLabelSr}</td>
                    <td class="num">${newBuild}</td>
                    <td class="num">${oldBuild}</td>
                    <td class="city-row-range">${range}</td>
                </tr>
                <tr class="city-card-mobile">
                    <td colspan="4">
                        <div class="city-card-mobile__inner">
                            <h3 class="city-card-mobile__title">${row.cityLabelSr}</h3>
                            <p class="city-card-mobile__row"><span>Novogradnja</span><strong>${newBuild}</strong></p>
                            <p class="city-card-mobile__row"><span>Starogradnja</span><strong>${oldBuild}</strong></p>
                            <p class="city-card-mobile__row"><span>Tipičan raspon</span><strong>${range}</strong></p>
                        </div>
                    </td>
                </tr>
            `;
        })
        .join('');
};

const getNominalSeriesByMetric = (points, metric) => {
    const config = metricConfig(metric);
    return makeLineSeries(points, [config.key]);
};

const pickCpiMetricValue = (entry, metric) => {
    if (!entry || typeof entry !== 'object') {
        return null;
    }

    if (metric === 'index') {
        return toNumber(entry?.index)
            ?? toNumber(entry?.indexValue)
            ?? toNumber(entry?.value)
            ?? toNumber(entry?.avg_cpi_index)
            ?? null;
    }

    if (metric === 'qoq') {
        const direct = toNumber(entry?.qoqPct)
            ?? toNumber(entry?.qoq)
            ?? toNumber(entry?.qoqChangePct)
            ?? toNumber(entry?.inflation_qoq_pct)
            ?? null;

        if (direct !== null) {
            return direct;
        }

        const isEstimatedLatestRow = Boolean(
            entry?.isEstimated === true
            || entry?.is_estimated === true
            || entry?.status === 'estimated'
        );

        if (!isEstimatedLatestRow) {
            return null;
        }

        return toNumber(entry?.yoyPct)
            ?? toNumber(entry?.inflation_yoy_q_avg_pct)
            ?? toNumber(entry?.inflation_yoy_pct)
            ?? null;
    }

    const config = metricConfig(metric);
    return toNumber(entry?.[config.key]) ?? null;
};

const getCpiSeriesByMetric = (cpiStore, metric) => {
    const quarterly = Array.isArray(cpiStore?.quarterly) ? cpiStore.quarterly : [];
    const yearly = Array.isArray(cpiStore?.yearly) ? cpiStore.yearly : [];
    const general = Array.isArray(cpiStore?.general) ? cpiStore.general : [];

    const sourcePriority = metric === 'qoq'
        ? [
            [quarterly, 3],
            [general, 1],
        ]
        : [
            [quarterly, 3],
            [yearly, 2],
            [general, 1],
        ];

    const byPeriod = new Map();

    sourcePriority.forEach(([rows, priority]) => {
        (Array.isArray(rows) ? rows : []).forEach((entry) => {
            if (!entry?.periodKey) {
                return;
            }

            const value = pickCpiMetricValue(entry, metric);
            const current = byPeriod.get(entry.periodKey) || {
                __priority: -Infinity,
            };
            const currentValue = pickCpiMetricValue(current, metric);
            const hasEstimatedRateFallback = metric === 'index' && (
                toNumber(entry?.yoyPct) !== null
                || toNumber(entry?.inflation_yoy_q_avg_pct) !== null
                || toNumber(entry?.inflation_yoy_pct) !== null
                || toNumber(entry?.inflation_avg_year_pct) !== null
            ) && (
                entry?.isEstimated === true
                || entry?.is_estimated === true
                || entry?.status === 'estimated'
            );

            if (value === null && currentValue === null && !hasEstimatedRateFallback) {
                return;
            }

            if (
                currentValue === null
                || priority > current.__priority
                || (priority === current.__priority && value !== null && currentValue === null)
            ) {
                byPeriod.set(entry.periodKey, {
                    ...current,
                    ...entry,
                    __priority: priority,
                });
            }
        });
    });

    const merged = Array.from(byPeriod.values()).sort((left, right) => (
        String(left.periodKey).localeCompare(String(right.periodKey))
    ));

    if (metric === 'index') {
        let previousKnownIndex = null;
        merged.forEach((entry) => {
            const directIndex = toNumber(entry?.index)
                ?? toNumber(entry?.indexValue)
                ?? toNumber(entry?.avg_cpi_index)
                ?? null;

            if (directIndex !== null) {
                previousKnownIndex = directIndex;
                return;
            }

            const estimatedRate = toNumber(entry?.yoyPct)
                ?? toNumber(entry?.inflation_yoy_q_avg_pct)
                ?? toNumber(entry?.inflation_yoy_pct)
                ?? toNumber(entry?.inflation_avg_year_pct)
                ?? null;
            const isEstimated = Boolean(
                entry?.isEstimated === true
                || entry?.is_estimated === true
                || entry?.status === 'estimated'
            );

            if (previousKnownIndex !== null && estimatedRate !== null && isEstimated) {
                const estimatedIndex = previousKnownIndex * (1 + (estimatedRate / 100));
                entry.index = estimatedIndex;
                previousKnownIndex = estimatedIndex;
            }
        });
    }

    return merged;
};

const buildComparablePairs = (points, cpiSeries) => {
    const lookup = new Map();
    (Array.isArray(cpiSeries) ? cpiSeries : []).forEach((entry) => {
        if (entry?.periodKey) {
            lookup.set(entry.periodKey, entry);
        }
    });

    return (Array.isArray(points) ? points : [])
        .map((point) => {
            const periodKey = periodKeyFromPoint(point);
            if (!periodKey || !lookup.has(periodKey)) {
                return null;
            }
            return {
                periodKey,
                nominal: point,
                cpi: lookup.get(periodKey),
                label:
                    firstText(point, ['periodLabel', 'quarter', 'reportPeriod'])
                    || firstText(lookup.get(periodKey), ['periodLabel'])
                    || '',
            };
        })
        .filter(Boolean);
};

const computeRealGrowthPct = (nominalGrowthPct, inflationPct) => {
    const nominal = toNumber(nominalGrowthPct);
    const inflation = toNumber(inflationPct);
    if (nominal === null || inflation === null || inflation <= -100) {
        return null;
    }

    return ((1 + nominal / 100) / (1 + inflation / 100) - 1) * 100;
};

const extractLastComparableValues = (labels, values) => {
    const finiteIndices = [];
    for (let i = 0; i < values.length; i += 1) {
        if (toNumber(values[i]) !== null) {
            finiteIndices.push(i);
        }
    }

    if (finiteIndices.length < 2) {
        return null;
    }

    const prevIndex = finiteIndices[finiteIndices.length - 2];
    const currentIndex = finiteIndices[finiteIndices.length - 1];
    return {
        prevLabel: labels[prevIndex],
        prevValue: values[prevIndex],
        currentLabel: labels[currentIndex],
        currentValue: values[currentIndex],
    };
};

const toCumulativePercentSeries = (values = []) => {
    let base = null;

    return (Array.isArray(values) ? values : []).map((value) => {
        const number = toNumber(value);
        if (number === null) {
            return null;
        }

        if (base === null) {
            base = number;
            return 0;
        }

        if (base === 0) {
            return null;
        }

        return ((number / base) - 1) * 100;
    });
};

const normalizeBenchmarkOverlapPoints = (rows) => {
    return (Array.isArray(rows) ? rows : [])
        .map((row) => {
            const period = normalizeText(row?.period);
            const periodKey = quarterKeyFromText(period);

            if (!periodKey) {
                return null;
            }

            return {
                periodKey,
                periodLabel: period,
                value: toNumber(row?.benchmarkValue),
            };
        })
        .filter(Boolean);
};

const normalizeBenchmarkSourceMeta = (body) => {
    const source = body?.source && typeof body.source === 'object' ? body.source : {};
    const sourceOrigin = firstText(source, ['sourceOrigin']) || firstText(body, ['sourceOrigin']);
    const effectiveSource = firstText(source, ['effectiveSource']) || firstText(body, ['sourceCodeSummary']);
    const fallbackUsed = Boolean(body?.fallbackUsed || source?.fallbackUsed || source?.storedFallback);
    const storedFallback = Boolean(source?.storedFallback || sourceOrigin === 'stored_raw_monthly' || sourceOrigin === 'stored_raw_daily');
    const hasMixedSources = Boolean(body?.hasMixedSources || source?.hasMixedSources);

    return {
        sourceOrigin,
        effectiveSource,
        fallbackUsed,
        storedFallback,
        hasMixedSources,
    };
};

const normalizeBenchmarkFreshnessMeta = (body) => {
    const freshness = body?.freshness && typeof body.freshness === 'object' ? body.freshness : {};

    return {
        stale: Boolean(body?.stale || freshness?.stale),
        staleReason: firstText(freshness, ['staleReason']),
        retryRecommended: Boolean(freshness?.retryRecommended),
        ageDays: toNumber(freshness?.ageDays),
        lastValidAt: firstText(freshness, ['lastValidAt']) || firstText(body, ['lastValidAt']),
    };
};

const normalizeBenchmarkNormalizationMeta = (body) => {
    const normalization = body?.normalization && typeof body.normalization === 'object'
        ? body.normalization
        : {};

    return {
        failedReason:
            firstText(normalization, ['failedReason'])
            || firstText(body, ['normalizationFailedReason']),
        droppedPoints:
            firstNumber(normalization, ['droppedPoints', 'droppedPointCount'])
            ?? firstNumber(body, ['normalizationDroppedPoints']),
    };
};

const normalizeBenchmarkValueMode = (body, benchmarkMode) => {
    const candidates = [
        body?.valueMode,
        body?.value_mode,
        body?.benchmarkMode,
        body?.benchmark_mode,
        body?.benchmark?.valueMode,
        body?.benchmark?.value_mode,
        body?.benchmark?.benchmarkMode,
        body?.benchmark?.benchmark_mode,
        body?.benchmark?.mode,
        body?.normalization?.valueMode,
        body?.normalization?.value_mode,
        benchmarkMode,
    ];

    for (let i = 0; i < candidates.length; i += 1) {
        const normalized = normalizeText(candidates[i]).toLowerCase();
        if (normalized && VALID_BENCHMARK_VALUE_MODES.includes(normalized)) {
            return normalized;
        }
    }

    return null;
};

const normalizeBenchmarkPayload = (body, metric, benchmarkMode, selectedBenchmark) => {
    const points = normalizeBenchmarkOverlapPoints(body?.overlapPoints);
    const availableMetrics = Array.isArray(body?.availableMetrics) ? body.availableMetrics : [];
    const supportsMetric = typeof body?.supportsMetric === 'boolean'
        ? body.supportsMetric
        : availableMetrics.includes(metric);
    const benchmarkDisabled = body?.benchmarkDisabled === true;
    const overlapCount = points.length;
    const valueMode = normalizeBenchmarkValueMode(body, benchmarkMode);
    const missingValueMode = valueMode === null;
    const renderable = !missingValueMode && !benchmarkDisabled && supportsMetric && overlapCount >= 2;
    const source = normalizeBenchmarkSourceMeta(body);
    const freshness = normalizeBenchmarkFreshnessMeta(body);
    const normalization = normalizeBenchmarkNormalizationMeta(body);

    return {
        benchmark: selectedBenchmark,
        benchmarkLabel: BENCHMARK_LABELS[selectedBenchmark] || selectedBenchmark.toUpperCase(),
        points,
        valueMode,
        renderable,
        supportsMetric,
        overlapCount,
        disableReasonCode: missingValueMode ? 'missing_value_mode' : firstText(body, ['disableReasonCode']),
        disableReasonText: firstText(body, ['disableReasonText']),
        disableReason: firstText(body, ['disableReason']),
        stale: freshness.stale,
        staleReason: freshness.staleReason,
        retryRecommended: freshness.retryRecommended,
        fallbackUsed: source.fallbackUsed,
        storedFallback: source.storedFallback,
        hasMixedSources: source.hasMixedSources,
        sourceOrigin: source.sourceOrigin,
        effectiveSource: source.effectiveSource,
        normalization,
        normalizationFailedReason: normalization.failedReason,
        normalizationDroppedPoints: normalization.droppedPoints,
        freshness,
        source,
        unavailable: false,
    };
};

const createUnavailableBenchmarkPayload = (benchmarkCode) => {
    return {
        benchmark: benchmarkCode,
        benchmarkLabel: BENCHMARK_LABELS[benchmarkCode] || benchmarkCode.toUpperCase(),
        points: [],
        valueMode: null,
        renderable: false,
        supportsMetric: true,
        overlapCount: 0,
        disableReasonCode: '',
        disableReasonText: '',
        disableReason: '',
        stale: false,
        staleReason: '',
        retryRecommended: false,
        fallbackUsed: false,
        storedFallback: false,
        hasMixedSources: false,
        sourceOrigin: '',
        effectiveSource: '',
        normalization: {
            failedReason: '',
            droppedPoints: null,
        },
        normalizationFailedReason: '',
        normalizationDroppedPoints: null,
        freshness: null,
        source: null,
        unavailable: true,
    };
};

const isBenchmarkSeriesUnavailable = (entry) => {
    if (!entry) {
        return true;
    }

    return entry.unavailable === true || entry.renderable === false;
};

const hasBenchmarkFallbackSource = (entry) => {
    if (!entry) {
        return false;
    }

    return Boolean(
        entry.fallbackUsed
        || entry.storedFallback
        || entry.source?.fallbackUsed
        || entry.source?.storedFallback
        || entry.sourceOrigin === 'stored_raw_monthly'
        || entry.sourceOrigin === 'stored_raw_daily'
    );
};

const hasBenchmarkStaleSignal = (entry) => {
    if (!entry) {
        return false;
    }

    return Boolean(entry.stale || entry.freshness?.stale);
};

const mapIndexSeriesByScale = (values, indexScale) => {
    if (indexScale === 'base100') {
        return values;
    }
    return toCumulativePercentSeries(values);
};

const buildLineDataset = (label, data, style, options = {}) => {
    return {
        label,
        data,
        benchmarkCode: options.benchmarkCode,
        isCpiOverlay: options.isCpiOverlay === true,
        hidden: options.hidden,
        yAxisID: options.yAxisID || 'y',
        borderColor: style.borderColor,
        borderWidth: options.borderWidth ?? 2,
        borderDash: options.borderDash || [],
        backgroundColor: style.backgroundColor,
        tension: options.tension ?? 0.26,
        cubicInterpolationMode: options.cubicInterpolationMode || 'default',
        pointRadius: options.pointRadius ?? 0,
        pointHoverRadius: options.pointHoverRadius ?? 3.8,
        pointHitRadius: options.pointHitRadius ?? 10,
        pointBackgroundColor: style.pointBackgroundColor,
        pointBorderColor: '#ffffff',
        pointBorderWidth: options.pointBorderWidth ?? 1.2,
        spanGaps: options.spanGaps === true,
    };
};

const benchmarkStyle = (benchmarkCode) => {
    if (benchmarkCode === 'gld') {
        return SERIES_STYLES.gld;
    }

    if (benchmarkCode === 'btc') {
        return SERIES_STYLES.btc;
    }

    return SERIES_STYLES.spy;
};

const resolveRequestedBenchmarks = (ui) => {
    if (!ui?.compare) {
        return [];
    }

    if (ui?.benchmark && ui.benchmark !== 'none') {
        return [ui.benchmark];
    }

    return COMPARATIVE_BENCHMARKS;
};

const buildNominalCpiOverlay = (points, metric, cpiStore, indexScale = 'cumulative_pct') => {
    const config = metricConfig(metric);
    const cpiSeries = getCpiSeriesByMetric(cpiStore, metric);
    const cpiLookup = new Map();
    const sortedSeries = (Array.isArray(cpiSeries) ? cpiSeries : []).slice().sort((left, right) => (
        String(left?.periodKey || '').localeCompare(String(right?.periodKey || ''))
    ));

    sortedSeries.forEach((entry) => {
        if (!entry?.periodKey) {
            return;
        }

        const value = pickCpiMetricValue(entry, metric);
        if (toNumber(value) !== null) {
            cpiLookup.set(entry.periodKey, value);
        }
    });

    if (metric === 'index') {
        let previousIndex = null;
        sortedSeries.forEach((entry) => {
            if (!entry?.periodKey) {
                return;
            }

            const directIndex = toNumber(entry?.index)
                ?? toNumber(entry?.indexValue)
                ?? toNumber(entry?.avg_cpi_index)
                ?? null;

            if (directIndex !== null) {
                previousIndex = directIndex;
                cpiLookup.set(entry.periodKey, directIndex);
                return;
            }

            const estimatedRate = toNumber(entry?.yoyPct)
                ?? toNumber(entry?.inflation_yoy_q_avg_pct)
                ?? toNumber(entry?.inflation_yoy_pct)
                ?? toNumber(entry?.inflation_avg_year_pct)
                ?? null;
            const isEstimatedLatestRow = Boolean(
                entry?.isEstimated === true
                || entry?.is_estimated === true
                || entry?.status === 'estimated'
            );

            if (previousIndex !== null && estimatedRate !== null && isEstimatedLatestRow) {
                cpiLookup.set(entry.periodKey, previousIndex * (1 + (estimatedRate / 100)));
            }
        });
    }

    const values = (Array.isArray(points) ? points : []).map((point) => {
        const periodKey = periodKeyFromPoint(point);
        if (!periodKey || !cpiLookup.has(periodKey)) {
            return null;
        }
        return cpiLookup.get(periodKey);
    });

    const displayValues = metric === 'index' ? mapIndexSeriesByScale(values, indexScale) : values;

    const alignedFiniteValues = displayValues.reduce((count, value) => (toNumber(value) === null ? count : count + 1), 0);
    const latestValue = toNumber(displayValues[displayValues.length - 1]);
    if (alignedFiniteValues === 0 || (alignedFiniteValues === 1 && latestValue === null)) {
        return null;
    }

    const label = `Inflacija ${config.label.toLowerCase()}`;

    return buildLineDataset(label, displayValues, SERIES_STYLES.cpi, {
        borderWidth: 2,
        borderDash: [7, 5],
        isCpiOverlay: true,
        spanGaps: true,
    });
};

const toBenchmarkLookup = (benchmarkData) => {
    const lookup = new Map();
    (Array.isArray(benchmarkData?.points) ? benchmarkData.points : []).forEach((entry) => {
        if (!entry?.periodKey || toNumber(entry?.value) === null) {
            return;
        }

        lookup.set(entry.periodKey, entry.value);
    });

    return lookup;
};

const countAlignedFiniteValues = (values) => {
    return values.reduce((count, value) => (toNumber(value) === null ? count : count + 1), 0);
};

const normalizeBenchmarkToIndexLevel = (value, valueMode) => {
    const numeric = toNumber(value);
    if (numeric === null) {
        return null;
    }

    if (valueMode === 'quarterly_pct') {
        return null;
    }

    if (valueMode === 'base100') {
        return numeric;
    }

    if (valueMode === 'cumulative_pct') {
        return 100 + numeric;
    }

    return null;
};

const toQuarterSortIndex = (periodKey) => {
    const match = /^(\d{4})-Q([1-4])$/i.exec(String(periodKey || '').trim());
    if (!match) {
        return null;
    }

    const year = Number(match[1]);
    const quarter = Number(match[2]);
    if (!Number.isFinite(year) || !Number.isFinite(quarter)) {
        return null;
    }

    return (year * 10) + quarter;
};

const hasContiguousQuarterSequence = (points) => {
    const sorted = (Array.isArray(points) ? points : [])
        .filter((entry) => entry?.periodKey && toNumber(entry?.value) !== null)
        .sort((a, b) => {
            const aSort = toQuarterSortIndex(a.periodKey);
            const bSort = toQuarterSortIndex(b.periodKey);
            if (aSort === null && bSort === null) {
                return String(a.periodKey).localeCompare(String(b.periodKey));
            }
            if (aSort === null) {
                return 1;
            }
            if (bSort === null) {
                return -1;
            }
            return aSort - bSort;
        });

    if (sorted.length < 2) {
        return false;
    }

    for (let index = 1; index < sorted.length; index += 1) {
        const previous = toQuarterSortIndex(sorted[index - 1].periodKey);
        const current = toQuarterSortIndex(sorted[index].periodKey);
        if (previous === null || current === null) {
            return false;
        }

        if (current - previous > 1) {
            return false;
        }
    }

    return true;
};

const reconstructLevelsFromQuarterlyReturns = (points) => {
    const sorted = (Array.isArray(points) ? points : [])
        .filter((entry) => entry?.periodKey && toNumber(entry?.value) !== null)
        .sort((a, b) => String(a.periodKey).localeCompare(String(b.periodKey)));

    if (!hasContiguousQuarterSequence(sorted) || sorted.length < 2) {
        return new Map();
    }

    const levels = new Map();
    let previousLevel = null;

    sorted.forEach((entry, index) => {
        const quarterlyReturn = toNumber(entry?.value);
        if (quarterlyReturn === null || Math.abs(quarterlyReturn) > 1000) {
            return;
        }

        if (index === 0) {
            previousLevel = 100;
            levels.set(entry.periodKey, previousLevel);
            return;
        }

        if (previousLevel === null) {
            return;
        }

        previousLevel = previousLevel * (1 + (quarterlyReturn / 100));
        levels.set(entry.periodKey, previousLevel);
    });

    return levels;
};

const buildBenchmarkDisplayValuesByPeriod = (benchmarkData, requestedValueMode) => {
    const dataValueMode = benchmarkData?.valueMode
        || benchmarkData?.benchmarkMode
        || benchmarkData?.benchmark_mode
        || benchmarkData?.mode
        || requestedValueMode;
    const rawPoints = Array.isArray(benchmarkData?.points) ? benchmarkData.points : [];

    if (!dataValueMode) {
        return new Map();
    }

    if (dataValueMode === requestedValueMode) {
        const direct = new Map();
        rawPoints.forEach((entry) => {
            const value = toNumber(entry?.value);
            if (!entry?.periodKey || value === null) {
                return;
            }
            direct.set(entry.periodKey, value);
        });
        return direct;
    }

    if (dataValueMode === 'quarterly_pct' && (requestedValueMode === 'cumulative_pct' || requestedValueMode === 'base100')) {
        return new Map();
    }

    if (dataValueMode === 'cumulative_pct' && requestedValueMode === 'base100') {
        const shifted = new Map();
        rawPoints.forEach((entry) => {
            const value = toNumber(entry?.value);
            if (!entry?.periodKey || value === null) {
                return;
            }
            shifted.set(entry.periodKey, 100 + value);
        });
        return shifted;
    }

    if (dataValueMode === 'base100' && requestedValueMode === 'cumulative_pct') {
        const shifted = new Map();
        rawPoints.forEach((entry) => {
            const value = toNumber(entry?.value);
            if (!entry?.periodKey || value === null) {
                return;
            }
            shifted.set(entry.periodKey, value - 100);
        });
        return shifted;
    }

    return new Map();
};

const buildBenchmarkNominalOverlays = (points, benchmarkSeries, requestedValueMode = 'cumulative_pct') => {
    return (Array.isArray(benchmarkSeries) ? benchmarkSeries : []).reduce((datasets, benchmarkData) => {
        if (!benchmarkData?.renderable) {
            return datasets;
        }

        const lookup = buildBenchmarkDisplayValuesByPeriod(benchmarkData, requestedValueMode);
        const values = (Array.isArray(points) ? points : []).map((point) => {
            const periodKey = periodKeyFromPoint(point);
            if (!periodKey || !lookup.has(periodKey)) {
                return null;
            }

            return lookup.get(periodKey);
        });

        if (countAlignedFiniteValues(values) < 2) {
            return datasets;
        }

        datasets.push(buildLineDataset(
            benchmarkData.benchmarkLabel,
            values,
            benchmarkStyle(benchmarkData.benchmark),
            {
                benchmarkCode: benchmarkData.benchmark,
                hidden: benchmarkData.defaultHidden === true,
                borderWidth: 2,
                borderDash: [6, 4],
                tension: 0,
                cubicInterpolationMode: 'monotone',
            }
        ));

        return datasets;
    }, []);
};

const maxAbsoluteFiniteValue = (values = []) => {
    return (Array.isArray(values) ? values : []).reduce((max, value) => {
        const numeric = toNumber(value);
        if (numeric === null) {
            return max;
        }

        const absolute = Math.abs(numeric);
        return absolute > max ? absolute : max;
    }, 0);
};

const applyDualAxisByDominance = (datasets = [], primaryDataset) => {
    const normalized = Array.isArray(datasets) ? datasets : [];
    if (!normalized.length) {
        return {
            datasets: normalized,
            axes: {
                y: { enabled: true },
                y1: { enabled: false },
            },
        };
    }

    const primaryMax = maxAbsoluteFiniteValue(primaryDataset?.data || []);
    if (primaryMax <= 0) {
        normalized.forEach((dataset) => {
            dataset.yAxisID = 'y';
        });
        return {
            datasets: normalized,
            axes: {
                y: { enabled: true },
                y1: { enabled: false },
            },
        };
    }

    let dualAxisEnabled = false;

    normalized.forEach((dataset, index) => {
        if (dataset?.hidden === true) {
            dataset.yAxisID = 'y';
            return;
        }

        if (index === 0) {
            dataset.yAxisID = 'y';
            return;
        }

        const isCpiOverlay = dataset?.isCpiOverlay === true;
        if (isCpiOverlay) {
            dataset.yAxisID = 'y';
            return;
        }

        const benchmarkCode = normalizeText(dataset?.benchmarkCode).toLowerCase();
        if (benchmarkCode !== 'btc') {
            dataset.yAxisID = 'y';
            return;
        }

        const benchmarkMax = maxAbsoluteFiniteValue(dataset?.data || []);
        const ratio = benchmarkMax / primaryMax;
        const isDominant = Number.isFinite(ratio) && ratio >= BENCHMARK_DOMINANCE_RATIO_THRESHOLD;
        dataset.yAxisID = isDominant ? 'y1' : 'y';
        if (isDominant) {
            dualAxisEnabled = true;
        }
    });

    return {
        datasets: normalized,
        axes: {
            y: { enabled: true },
            y1: { enabled: dualAxisEnabled },
        },
    };
};

const buildBenchmarkRealOverlays = (comparable, benchmarkSeries, metric, indexScale = 'cumulative_pct') => {
    const comparablePairs = Array.isArray(comparable) ? comparable : [];

    return (Array.isArray(benchmarkSeries) ? benchmarkSeries : []).reduce((datasets, benchmarkData) => {
        if (!benchmarkData?.renderable) {
            return datasets;
        }

        const valueMode = benchmarkData?.valueMode || indexScale || 'cumulative_pct';
        const lookup = buildBenchmarkDisplayValuesByPeriod(benchmarkData, metric === 'index' ? 'base100' : valueMode);
        let values = [];

        if (metric === 'index') {
            const first = comparablePairs.find((pair) => {
                const periodKey = normalizeText(pair?.periodKey);
                const benchmarkLevel = periodKey ? toNumber(lookup.get(periodKey)) : null;
                const cpiIndex = toNumber(pair?.cpi?.index);
                return benchmarkLevel !== null && cpiIndex !== null && cpiIndex !== 0;
            });

            if (!first) {
                return datasets;
            }

            const cpiBase = toNumber(first.cpi?.index);
            if (cpiBase === null || cpiBase === 0) {
                return datasets;
            }

            const realLevels = comparablePairs.map((pair) => {
                const periodKey = normalizeText(pair?.periodKey);
                const benchmarkLevel = periodKey ? toNumber(lookup.get(periodKey)) : null;
                const cpiIndex = toNumber(pair?.cpi?.index);
                if (benchmarkLevel === null || cpiIndex === null || cpiIndex === 0) {
                    return null;
                }

                return benchmarkLevel / (cpiIndex / cpiBase);
            });

            values = indexScale === 'base100'
                ? realLevels
                : toCumulativePercentSeries(realLevels);
        } else {
            values = comparablePairs.map((pair) => {
                const periodKey = normalizeText(pair?.periodKey);
                const benchmarkRaw = periodKey ? lookup.get(periodKey) : null;
                const inflationPct = pair?.cpi?.[metricConfig(metric).key] ?? null;
                return computeRealGrowthPct(benchmarkRaw, inflationPct);
            });
        }

        if (countAlignedFiniteValues(values) < 2) {
            return datasets;
        }

        datasets.push(buildLineDataset(
            `${benchmarkData.benchmarkLabel} (usklađeno sa inflacijom)`,
            values,
            benchmarkStyle(benchmarkData.benchmark),
            {
                benchmarkCode: benchmarkData.benchmark,
                hidden: benchmarkData.defaultHidden === true,
                borderWidth: 2,
                borderDash: [6, 4],
                tension: 0,
                cubicInterpolationMode: 'monotone',
            }
        ));

        return datasets;
    }, []);
};

const buildBenchmarkNote = (ui, benchmarkSeries) => {
    if (!ui || !ui.compare) {
        return '';
    }

    const series = Array.isArray(benchmarkSeries) ? benchmarkSeries : [];
    if (!series.length) {
        return BENCHMARK_UNAVAILABLE_NOTE;
    }

    const unavailable = series
        .filter((item) => isBenchmarkSeriesUnavailable(item))
        .map((item) => item?.benchmarkLabel)
        .filter(Boolean);

    const fallback = series
        .filter((item) => !isBenchmarkSeriesUnavailable(item) && hasBenchmarkFallbackSource(item))
        .map((item) => item?.benchmarkLabel)
        .filter(Boolean);

    const stale = series
        .filter((item) => !isBenchmarkSeriesUnavailable(item) && hasBenchmarkStaleSignal(item))
        .map((item) => item?.benchmarkLabel)
        .filter(Boolean);

    if (unavailable.length === series.length) {
        return BENCHMARK_UNAVAILABLE_NOTE;
    }

    const notes = [];

    if (unavailable.length > 0) {
        notes.push(`${COMPARISON_PARTIAL_NOTE}: ${unavailable.join(', ')} nije dostupan.`);
    }


    if (stale.length > 0) {
        notes.push(stale.length === series.length
            ? BENCHMARK_STALE_NOTE
            : `Za ${stale.join(', ')} podaci su zastareli.`);
    }

    return notes.join(' ');
};

const buildNominalChartModel = (payload, metric, cpiStore, benchmarkSeries, indexScale = 'cumulative_pct') => {
    const config = metricConfig(metric);
    const points = payload?.nationalTrend?.points || [];
    const series = getNominalSeriesByMetric(points, metric);
    const displayValues = metric === 'index' ? mapIndexSeriesByScale(series.values, indexScale) : series.values;
    const displaySeries = { labels: series.labels, values: displayValues };

    if (!isSeriesEligible(displaySeries, 2)) {
        return null;
    }

    const comparable = extractLastComparableValues(series.labels, displayValues);
    const cpiOverlay = buildNominalCpiOverlay(points, metric, cpiStore, indexScale);
    const benchmarkOverlays = buildBenchmarkNominalOverlays(points, benchmarkSeries, indexScale);
    const datasets = [buildLineDataset(
        `${config.label} (${SEGMENT_LABELS[payload?.query?.segment] || SEGMENT_LABELS.total})`,
        displayValues,
        SERIES_STYLES.marketNominal,
        {
            yAxisID: 'y',
            borderWidth: 2.8,
            pointRadius: (ctx) => {
                const dataLength = ctx?.dataset?.data?.length || 0;
                return ctx.dataIndex === dataLength - 1 ? 3.8 : 0;
            },
            pointHoverRadius: 4.6,
            pointHitRadius: 12,
            pointBorderWidth: 1.5,
        }
    )];

    if (cpiOverlay) {
        cpiOverlay.yAxisID = 'y';
        datasets.push(cpiOverlay);
    }

    datasets.push(...benchmarkOverlays);
    const withAxes = applyDualAxisByDominance(datasets, datasets[0]);

    const indexAsBase = metric === 'index' && indexScale === 'base100';
    const yValueMode = indexAsBase ? 'number' : (config.suffix === '%' ? 'percent' : 'number');
    const ySuffix = indexAsBase ? '' : config.suffix;

    return {
        type: 'line',
        labels: series.labels,
        datasets: withAxes.datasets,
        ySuffix,
        yValueMode,
        axes: {
            y: {
                enabled: true,
                valueMode: yValueMode,
                suffix: ySuffix,
            },
            y1: {
                enabled: withAxes.axes.y1.enabled,
                valueMode: yValueMode,
                suffix: ySuffix,
            },
        },
        annotationContext: comparable
            ? {
                mode: 'nominal',
                metric,
                ...comparable,
            }
            : null,
    };
};

const buildRealChartModel = (payload, metric, cpiStore, benchmarkSeries = [], indexScale = 'cumulative_pct') => {
    const points = payload?.nationalTrend?.points || [];
    const cpiSeries = getCpiSeriesByMetric(cpiStore, metric);
    const comparable = buildComparablePairs(points, cpiSeries);
    if (comparable.length < 2) {
        return null;
    }

    const labels = comparable.map((pair) => pair.label || pair.cpi?.periodLabel || '');
    let values = [];
    let ySuffix = '%';
    let yValueMode = 'percent';

    if (metric === 'index') {
        const first = comparable.find((pair) => toNumber(pair.nominal?.index) !== null && toNumber(pair.cpi?.index) !== null);
        if (!first) {
            return null;
        }
        const cpiBase = toNumber(first.cpi.index);
        if (cpiBase === null || cpiBase === 0) {
            return null;
        }

        const realIndexLevels = comparable.map((pair) => {
            const nominalIndex = toNumber(pair.nominal?.index);
            const cpiIndex = toNumber(pair.cpi?.index);
            if (nominalIndex === null || cpiIndex === null || cpiIndex === 0) {
                return null;
            }
            return nominalIndex / (cpiIndex / cpiBase);
        });

        if (indexScale === 'base100') {
            values = realIndexLevels;
            ySuffix = '';
            yValueMode = 'number';
        } else {
            values = toCumulativePercentSeries(realIndexLevels);
            ySuffix = '%';
            yValueMode = 'percent';
        }
    } else {
        values = comparable.map((pair) => {
            const nominalPct = metricValueFromPoint(pair.nominal, metric);
            const inflationPct = pair.cpi?.[metricConfig(metric).key] ?? null;
            return computeRealGrowthPct(nominalPct, inflationPct);
        });
    }

    const series = { labels, values };
    if (!isSeriesEligible(series, 2)) {
        return null;
    }

    const last = extractLastComparableValues(labels, values);

    const datasets = [buildLineDataset(
        metric === 'index'
            ? 'Cene nekretnina (usklađeno sa inflacijom)'
            : `Realna ${metricConfig(metric).label.toLowerCase()} (usklađeno sa inflacijom)`,
        values,
        SERIES_STYLES.marketReal,
        {
            yAxisID: 'y',
            borderWidth: 2.8,
            pointRadius: (ctx) => {
                const dataLength = ctx?.dataset?.data?.length || 0;
                return ctx.dataIndex === dataLength - 1 ? 3.8 : 0;
            },
            pointHoverRadius: 4.6,
            pointHitRadius: 12,
            pointBorderWidth: 1.5,
        }
    )];

    const benchmarkOverlays = buildBenchmarkRealOverlays(comparable, benchmarkSeries, metric, indexScale);
    datasets.push(...benchmarkOverlays);
    const withAxes = applyDualAxisByDominance(datasets, datasets[0]);

    return {
        type: 'line',
        labels,
        datasets: withAxes.datasets,
        ySuffix,
        yValueMode,
        axes: {
            y: {
                enabled: true,
                valueMode: yValueMode,
                suffix: ySuffix,
            },
            y1: {
                enabled: withAxes.axes.y1.enabled,
                valueMode: yValueMode,
                suffix: ySuffix,
            },
        },
        annotationContext: last
            ? {
                mode: 'real',
                metric,
                ...last,
            }
            : null,
    };
};

const buildChartModel = (payload, metric, mode = 'nominal', cpiStore = {}, benchmarkSeries = [], indexScale = 'cumulative_pct') => {
    if (mode === 'real') {
        return buildRealChartModel(payload, metric, cpiStore, benchmarkSeries, indexScale);
    }

    return buildNominalChartModel(payload, metric, cpiStore, benchmarkSeries, indexScale);
};

const buildChartAnnotation = (chartModel) => {
    const annotation = chartModel?.annotationContext;
    if (!annotation) {
        return '';
    }

    const modeLabel = MODE_LABELS[annotation.mode] || MODE_LABELS.nominal;
    const metric = annotation.metric;
    const valueFormatter = (val) => {
        if (chartModel?.yValueMode === 'percent') {
            return percentFormat(val, metricConfig(metric).decimals, true);
        }

        return decimalFormat(val, 2);
    };

    return `${modeLabel}: ${annotation.prevLabel}: ${valueFormatter(annotation.prevValue)}  ->  ${annotation.currentLabel}: ${valueFormatter(annotation.currentValue)}`;
};

const formatSummaryValue = (value, chartModel) => {
    if (chartModel?.yValueMode === 'percent') {
        return percentFormat(value, 2, true);
    }

    return decimalFormat(value, 2);
};

const renderChartSummary = (chartModel) => {
    const footer = document.getElementById('chart-summary-footer');
    const list = document.getElementById('chart-summary-rows');
    if (!footer || !list) {
        return false;
    }

    if (!chartModel || !Array.isArray(chartModel?.datasets)) {
        footer.hidden = true;
        list.replaceChildren();
        return false;
    }

    const labels = Array.isArray(chartModel.labels) ? chartModel.labels : [];
    const rows = chartModel.datasets
        .map((dataset) => {
            const comparable = extractLastComparableValues(labels, dataset?.data || []);
            if (!comparable) {
                return null;
            }

            return {
                label: normalizeText(dataset?.label),
                prevLabel: normalizeText(comparable.prevLabel),
                currentLabel: normalizeText(comparable.currentLabel),
                prevValue: formatSummaryValue(comparable.prevValue, chartModel),
                currentValue: formatSummaryValue(comparable.currentValue, chartModel),
            };
        })
        .filter(Boolean);

    if (!rows.length) {
        footer.hidden = true;
        list.replaceChildren();
        return false;
    }

    list.replaceChildren();
    rows.forEach((row) => {
        const li = document.createElement('li');
        const strong = document.createElement('strong');
        const span = document.createElement('span');

        strong.textContent = `${row.label}:`;
        span.textContent = `prethodni kvartal ${row.prevLabel}: ${row.prevValue}, trenutni kvartal ${row.currentLabel}: ${row.currentValue}`;

        li.append(strong, span);
        list.appendChild(li);
    });

    footer.hidden = false;
    return true;
};

const buildModeEmptyState = (mode, payload = null) => {
    if (payload?.availability?.requestedScope === 'large_city_q4_snapshot' || payload?.query?.scope === 'large_city_q4_snapshot') {
        return 'Za veće gradove dostupan je samo presek cena, bez istorijskog trenda.';
    }
    if (mode === 'real') {
        return 'Nema dovoljno podataka za grafički prikaz izabrane kombinacije. Inflacija nije dostupna za izabrani režim.';
    }
    return 'Nema dovoljno podataka za grafički prikaz izabrane kombinacije.';
};

const renderChart = (payload, metric, mode, chartStore, cpiStore, benchmarkSeries, ui) => {
    chartStore.renderToken = Number.isFinite(chartStore.renderToken)
        ? chartStore.renderToken + 1
        : 1;
    const renderToken = chartStore.renderToken;

    const canvas = document.getElementById('chart-dominant-story');
    const empty = document.getElementById('chart-empty');
    const note = document.getElementById('chart-annotation');
    const benchmarkNote = document.getElementById('chart-benchmark-note');
    if (!canvas || !empty || !note || !benchmarkNote) {
        return;
    }

    destroyChart(chartStore);
    const safeModel = buildChartModel(payload, metric, mode, cpiStore, benchmarkSeries, ui?.benchmarkMode || 'cumulative_pct');
    const benchmarkMessage = buildBenchmarkNote(ui, benchmarkSeries);

    benchmarkNote.textContent = benchmarkMessage;
    benchmarkNote.hidden = !benchmarkMessage;

    if (!safeModel) {
        empty.hidden = false;
        empty.textContent = buildModeEmptyState(mode, payload);
        note.hidden = true;
        renderChartSummary(null);
        return;
    }

    empty.hidden = true;
    deferInit(canvas, () => {
        if (renderToken !== chartStore.renderToken) {
            return;
        }

        if (window.Chart && typeof window.Chart.getChart === 'function') {
            const existingCanvasChart = window.Chart.getChart(canvas);
            if (existingCanvasChart && typeof existingCanvasChart.destroy === 'function') {
                existingCanvasChart.destroy();
            }
        }

        chartStore.current = createOfficialChart(canvas, safeModel);
    });

    const annotation = buildChartAnnotation(safeModel);
    const hasSummaryRows = renderChartSummary(safeModel);
    note.textContent = annotation;
    note.hidden = !annotation || hasSummaryRows;
};

const setBusy = (busy) => {
    const root = document.querySelector('.market-page');
    if (root) {
        root.setAttribute('aria-busy', busy ? 'true' : 'false');
    }
};

const wireAccordion = () => {
    const trigger = document.querySelector('.accordion-trigger');
    const panel = document.getElementById('methodology-panel');
    if (!trigger || !panel) {
        return;
    }

    let expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(expanded));
    panel.hidden = !expanded;

    trigger.addEventListener('click', () => {
        expanded = !expanded;
        trigger.setAttribute('aria-expanded', String(expanded));
        panel.hidden = !expanded;
    });
};

const bindSegmentSwitch = ({ state, onChange }) => {
    const group = document.querySelector('[data-role="segment-switch"]');
    if (!group) {
        return;
    }

    group.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-value]');
        if (!button) {
            return;
        }

        const value = normalizeText(button.getAttribute('data-value')).toLowerCase();
        if (!value || state.segment === value) {
            return;
        }

        state.segment = value;
        Object.assign(state, toState(state, DEFAULTS));
        onChange();
    });
};

const bindMetricSwitch = ({ ui, onChange }) => {
    const group = document.querySelector('[data-role="metric-switch"]');
    if (!group) {
        return;
    }

    group.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-value]');
        if (!button) {
            return;
        }

        const value = normalizeText(button.getAttribute('data-value')).toLowerCase();
        if (!value || ui.metric === value) {
            return;
        }

        ui.metric = value;
        onChange();
    });
};

const bindModeSwitch = ({ ui, onChange }) => {
    const group = document.querySelector('[data-role="mode-switch"]');
    if (!group) {
        return;
    }

    group.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-value]');
        if (!button) {
            return;
        }

        const value = normalizeText(button.getAttribute('data-value')).toLowerCase();
        if (!value || ui.mode === value) {
            return;
        }

        ui.mode = value;
        onChange();
    });
};

const bindComparisonToggle = ({ ui, onChange }) => {
    const toggle = document.getElementById('comparison-toggle-input');
    if (!toggle) {
        return;
    }

    toggle.addEventListener('change', () => {
        const checked = Boolean(toggle.checked);
        if (ui.compare === checked) {
            return;
        }

        ui.compare = checked;
        toggle.setAttribute('aria-checked', checked ? 'true' : 'false');
        onChange();
    });
};

const buildLocationSelect = (payload) => {
    const regionsGroup = document.getElementById('location-optgroup-regions');
    if (!regionsGroup) {
        return;
    }

    regionsGroup.innerHTML = (Array.isArray(payload?.regionalRows) ? payload.regionalRows : [])
        .filter((row) => row.scopeCode && row.scopeLabelSr)
        .map((row) => `<option value="region:${row.scopeCode}">${row.scopeLabelSr}</option>`)
        .join('');
};

const syncLocationSelect = (state) => {
    const select = document.getElementById('location-select');
    if (!select) {
        return;
    }

    let value = 'serbia';
    if (state.scope === 'statistical_region' && state.region) {
        value = `region:${state.region}`;
    }

    select.value = value;
};

const bindLocationSelect = ({ state, segmentPayloads, ui, onChange }) => {
    const select = document.getElementById('location-select');
    if (!select) {
        return;
    }

    select.addEventListener('change', () => {
        const value = select.value;

        if (value === 'serbia') {
            state.scope = 'serbia';
            state.region = '';
            state.city = '';
        } else if (value.startsWith('region:')) {
            state.scope = 'statistical_region';
            state.region = value.slice('region:'.length);
            state.city = '';
        } else {
            return;
        }

        // Clear segment cache — scope changed, old payloads are stale
        Object.keys(segmentPayloads).forEach((k) => {
            delete segmentPayloads[k];
        });

        onChange();
    });
};

const renderAll = ({ activePayload, segmentPayloads, ui, state, chartStore, cpiStore, benchmarkStore }) => {
    renderHero(activePayload);
    renderCompareStrip(segmentPayloads);
    renderRegionTable(activePayload);
    renderCityTable(activePayload);
    renderChart(activePayload, ui.metric, ui.mode, chartStore, cpiStore, benchmarkStore.current, ui);
    syncMetricSwitch(ui.metric);
    syncModeSwitch(ui.mode);
    syncComparisonToggle(ui);
    syncSegmentSwitch(state);
    syncLocationSelect(state);
};

document.addEventListener('DOMContentLoaded', () => {
    const copy = parseJsonNode('market-copy-data', {});
    const overview = parseJsonNode('market-overview-data', {});
    const insights = parseJsonNode('market-insights-data', {});
    const shareSnippet = parseJsonNode('market-share-data', {});
    const normalizedQuery = parseJsonNode('market-normalized-query-data', {});
    const renderVersion = normalizeText(document.querySelector('.market-page')?.getAttribute('data-render-version'));

    const initialPayload = normalizePayload({ overview, insights, shareSnippet, copy, query: normalizedQuery, renderVersion });
    const locationState = parseFromLocation(window.location, DEFAULTS).state;
    const state = toState({ ...initialPayload.query, ...locationState }, DEFAULTS);
    const legacyBenchmarkEnabled = locationState.benchmark && locationState.benchmark !== 'none';
    const ui = {
        metric: locationState.metric || 'yoy',
        mode: locationState.mode || 'nominal',
        compare: Boolean(locationState.compare || legacyBenchmarkEnabled),
        benchmark: locationState.benchmark || 'none',
        benchmarkMode: locationState.benchmarkMode || 'cumulative_pct',
    };
    enforceUiBenchmarkMode(ui);
    const chartStore = { current: null };
    const benchmarkStore = {
        cache: new Map(),
        current: [],
    };
    const cpiStore = {
        general: undefined,
        quarterly: undefined,
        yearly: undefined,
    };

    const segmentPayloads = {
        total: initialPayload,
        [state.segment]: initialPayload,
    };

    let activePayload = segmentPayloads[state.segment] || initialPayload;

    const ensureBenchmarkData = async () => {
        if (!ui.compare) {
            benchmarkStore.current = [];
            return [];
        }

        const requestedBenchmarks = resolveRequestedBenchmarks(ui);
        const resolvedSeries = await Promise.all(requestedBenchmarks.map(async (benchmarkCode) => {
            const key = benchmarkRequestKey(state, ui.metric, ui.mode, benchmarkCode, ui.benchmarkMode);
            const defaultHidden = ui.benchmark === 'none' && benchmarkCode === 'btc';
            const cached = benchmarkStore.cache.get(key);
            const nowMs = Date.now();

            if (cached && cached.negative && cached.expiresAt !== null && cached.expiresAt <= nowMs) {
                benchmarkStore.cache.delete(key);
            } else if (cached) {
                return {
                    ...cached.value,
                    defaultHidden,
                };
            }

            try {
                const response = await fetch(createBenchmarkApiUrl(
                    state,
                    ui.metric,
                    ui.mode,
                    benchmarkCode,
                    ui.benchmarkMode
                ));

                if (!response.ok) {
                    const unavailable = createUnavailableBenchmarkPayload(benchmarkCode);
                    benchmarkStore.cache.set(key, {
                        value: unavailable,
                        negative: true,
                        expiresAt: nowMs + BENCHMARK_NEGATIVE_CACHE_TTL_MS,
                    });
                    return unavailable;
                }

                const body = await response.json();
                const normalized = normalizeBenchmarkPayload(body, ui.metric, ui.benchmarkMode, benchmarkCode);
                benchmarkStore.cache.set(key, {
                    value: normalized,
                    negative: false,
                    expiresAt: null,
                });
                return {
                    ...normalized,
                    defaultHidden,
                };
            } catch (_error) {
                const unavailable = createUnavailableBenchmarkPayload(benchmarkCode);
                benchmarkStore.cache.set(key, {
                    value: unavailable,
                    negative: true,
                    expiresAt: nowMs + BENCHMARK_NEGATIVE_CACHE_TTL_MS,
                });
                return unavailable;
            }
        }));

        benchmarkStore.current = resolvedSeries;
        return resolvedSeries;
    };

    const refreshCpiForActiveChart = async (metric) => {
        await ensureCpiData(cpiStore, metric, {
            latestPeriodKey: latestMarketPeriodKeyFromPayload(activePayload),
            forceQuarterly: true,
        });
    };

    const refreshUiModeAndMetric = async () => {
        enforceUiBenchmarkMode(ui);
        applyHistory({
            ...state,
            metric: ui.metric,
            mode: ui.mode,
            compare: ui.compare,
            benchmark: ui.benchmark,
            benchmarkMode: ui.benchmarkMode,
        }, DEFAULTS, {
            mode: 'replace',
            pathname: '/market',
            hash: window.location.hash,
            includeDefaults: false,
        });

        try {
            await refreshCpiForActiveChart(ui.metric);
            await ensureBenchmarkData();
        } finally {
            renderAll({ activePayload, segmentPayloads, ui, state, chartStore, cpiStore, benchmarkStore });
        }
    };

    const ensureSegmentPayload = async (segment) => {
        if (segmentPayloads[segment]) {
            return segmentPayloads[segment];
        }

        const payload = await fetchPayload({ ...state, segment }, activePayload);
        segmentPayloads[segment] = payload;
        return payload;
    };

    const refreshSegment = async (options = {}) => {
        setBusy(true);
        if (options.syncHistory !== false) {
            applyHistory({
                ...state,
                metric: ui.metric,
                mode: ui.mode,
                compare: ui.compare,
                benchmark: ui.benchmark,
                benchmarkMode: ui.benchmarkMode,
            }, DEFAULTS, {
                mode: options.historyMode === 'replace' ? 'replace' : 'push',
                pathname: '/market',
                hash: window.location.hash,
                includeDefaults: false,
            });
        }

        try {
            activePayload = await ensureSegmentPayload(state.segment);
            await refreshCpiForActiveChart(ui.metric);
            await ensureBenchmarkData();
            renderAll({ activePayload, segmentPayloads, ui, state, chartStore, cpiStore, benchmarkStore });
            if (options.primeCompare) {
                primeCompareSegments();
            }
        } catch (_error) {
            const empty = document.getElementById('chart-empty');
            if (empty) {
                empty.hidden = false;
                empty.textContent = 'Osvežavanje podataka nije uspelo. Prikazan je poslednji raspoloživ prikaz.';
            }
        } finally {
            setBusy(false);
        }
    };

    const primeCompareSegments = async () => {
        const required = ['new_build', 'old_build'];
        const jobs = required
            .filter((segment) => !segmentPayloads[segment])
            .map(async (segment) => {
                try {
                    segmentPayloads[segment] = await fetchPayload({ ...state, segment }, activePayload);
                } catch (_error) {
                    return null;
                }
                return true;
            });

        if (jobs.length) {
            await Promise.all(jobs);
            renderAll({ activePayload, segmentPayloads, ui, state, chartStore, cpiStore, benchmarkStore });
        }
    };

    bindSegmentSwitch({
        state,
        onChange: () => {
            refreshSegment();
        },
    });

    bindMetricSwitch({
        ui,
        onChange: () => {
            refreshUiModeAndMetric();
        },
    });

    bindModeSwitch({
        ui,
        onChange: () => {
            refreshUiModeAndMetric();
        },
    });

    bindComparisonToggle({
        ui,
        onChange: () => {
            refreshUiModeAndMetric();
        },
    });

    wireAccordion();

    buildLocationSelect(initialPayload);
    bindLocationSelect({
        state,
        segmentPayloads,
        ui,
        onChange: () => {
            refreshSegment({ primeCompare: true });
        },
    });

    window.addEventListener('popstate', () => {
        const restored = parseFromLocation(window.location, DEFAULTS).state;
        const scopeChanged = restored.scope !== state.scope || restored.region !== state.region;
        Object.assign(state, restored);
        ui.metric = restored.metric || 'yoy';
        ui.mode = restored.mode || 'nominal';
        ui.compare = Boolean(restored.compare || (restored.benchmark && restored.benchmark !== 'none'));
        ui.benchmark = restored.benchmark || 'none';
        ui.benchmarkMode = restored.benchmarkMode || 'cumulative_pct';
        enforceUiBenchmarkMode(ui);
        if (scopeChanged) {
            Object.keys(segmentPayloads).forEach((k) => { delete segmentPayloads[k]; });
        }
        refreshSegment({ syncHistory: false, primeCompare: scopeChanged });
    });

    (async () => {
        try {
            await refreshCpiForActiveChart(ui.metric);
            await ensureBenchmarkData();
        } finally {
            renderAll({ activePayload, segmentPayloads, ui, state, chartStore, cpiStore, benchmarkStore });
        }
        primeCompareSegments();
    })();
});

const buildViewModel = ({ activePayload, segmentPayloads, state, metric }) => {
    return {
        activePayload,
        segmentPayloads,
        state,
        metric,
    };
};

const exported = {
    normalizeIndependentHeroFacts,
    extractHeroFacts,
    renderHero,
    normalizePayload,
    normalizeCpiSeries,
    requestCpiEndpoint,
    CPI_FETCH_OPTIONS,
    createCpiStore,
    mergeCpiSeries,
    ensureCpiData,
    latestMarketPeriodKeyFromPayload,
    normalizeBenchmarkPayload,
    normalizeBenchmarkValueMode,
    normalizeBenchmarkToIndexLevel,
    buildBenchmarkDisplayValuesByPeriod,
    reconstructLevelsFromQuarterlyReturns,
    buildBenchmarkNominalOverlays,
    buildBenchmarkRealOverlays,
    buildViewModel,
    buildShareableMarketUrl,
    buildChartModel,
    computeRealGrowthPct,
    buildBenchmarkNote,
    renderRegionTable,
    previousQuarterLabel,
    quarterDisplayLabel,
    BENCHMARK_NEGATIVE_CACHE_TTL_MS,
};

if (typeof module !== 'undefined' && module.exports) {
    module.exports = exported;
}

export {
    normalizeIndependentHeroFacts,
    extractHeroFacts,
    renderHero,
    normalizePayload,
    normalizeCpiSeries,
    requestCpiEndpoint,
    CPI_FETCH_OPTIONS,
    mergeCpiSeries,
    createCpiStore,
    ensureCpiData,
    latestMarketPeriodKeyFromPayload,
    normalizeBenchmarkPayload,
    normalizeBenchmarkValueMode,
    normalizeBenchmarkToIndexLevel,
    buildBenchmarkDisplayValuesByPeriod,
    reconstructLevelsFromQuarterlyReturns,
    buildBenchmarkNominalOverlays,
    buildBenchmarkRealOverlays,
    buildViewModel,
    buildShareableMarketUrl,
    buildChartModel,
    computeRealGrowthPct,
    buildBenchmarkNote,
    renderRegionTable,
    previousQuarterLabel,
    quarterDisplayLabel,
    BENCHMARK_NEGATIVE_CACHE_TTL_MS,
};
